import { useState, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { previewMigration, commitMigration } from '../services/api';
import { toast } from 'sonner';
import {
  Upload, FileText, ArrowRight, ArrowLeft, Check, X, Sparkles, AlertCircle, Database,
} from 'lucide-react';

const CANONICAL_FIELDS = [
  { key: 'date', label: 'Transaction Date', required: true },
  { key: 'vendor', label: 'Vendor / Payee', required: false },
  { key: 'amount', label: 'Amount', required: true },
  { key: 'description', label: 'Description / Memo', required: false },
  { key: 'account', label: 'Account / Category', required: false },
  { key: 'reference', label: 'Reference / Check #', required: false },
];

const STEPS = [
  { num: 1, label: 'Upload' },
  { num: 2, label: 'Map Columns' },
  { num: 3, label: 'Review & Import' },
  { num: 4, label: 'Done' },
];

export default function ImportBotkeeperPage() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [staging, setStaging] = useState(null);
  const [mapping, setMapping] = useState({});
  const [target, setTarget] = useState('transactions');
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileSelect = (f) => {
    if (!f) return;
    if (!/\.(csv|tsv|txt)$/i.test(f.name)) {
      toast.error('Please upload a .csv file');
      return;
    }
    setFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('source', 'botkeeper');
      const res = await previewMigration(fd);
      setStaging(res.data);
      setMapping(res.data.detected_mapping || {});
      setStep(2);
      toast.success(`Parsed ${res.data.total_rows} rows`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleCommit = async () => {
    setCommitting(true);
    try {
      const res = await commitMigration({
        staging_id: staging.staging_id,
        mapping,
        target,
      });
      setResult(res.data);
      setStep(4);
      toast.success(`Imported ${res.data.summary.created} records`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setCommitting(false);
    }
  };

  const requiredMapped =
    target !== 'transactions' || (mapping.date && mapping.amount);

  // Pre-compute the filtered mapping pairs once per mapping change,
  // avoiding map+filter on every render of step 3.
  const mappedPairs = useMemo(
    () => Object.entries(mapping).filter(([, v]) => v),
    [mapping]
  );

  return (
    <Layout title="Import from Botkeeper">
      <div data-testid="import-page" className="max-w-4xl mx-auto space-y-8">
        {/* Stepper */}
        <div className="flex items-center justify-between" data-testid="stepper">
          {STEPS.map((s, idx) => {
            const isActive = step === s.num;
            const isDone = step > s.num;
            return (
              <div key={s.num} className="flex items-center flex-1 last:flex-none">
                <div className="flex flex-col items-center">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center font-heading text-[14px] font-semibold transition-colors"
                    style={{
                      background: isDone ? '#0A3622' : isActive ? '#D98A4A' : '#E5E3DB',
                      color: isDone || isActive ? '#FFFFFF' : '#9CA3AF',
                    }}
                  >
                    {isDone ? <Check size={16} /> : s.num}
                  </div>
                  <p className="text-[11px] font-body font-medium mt-2" style={{ color: isActive ? '#0A3622' : '#4B5563' }}>{s.label}</p>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className="flex-1 h-0.5 mx-2 mb-6" style={{ background: isDone ? '#0A3622' : '#E5E3DB' }} />
                )}
              </div>
            );
          })}
        </div>

        {/* STEP 1 — Upload */}
        {step === 1 && (
          <section className="rounded-2xl p-10 text-center" style={{ background: '#FFFFFF', border: '1px solid #E5E3DB' }} data-testid="step-1">
            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(217,138,74,0.12)' }}>
              <Database size={28} style={{ color: '#D98A4A' }} strokeWidth={1.7} />
            </div>
            <h2 className="font-heading text-[26px] font-semibold tracking-tight mb-2" style={{ color: '#0A3622' }}>
              Bring your books over from Botkeeper.
            </h2>
            <p className="text-[15px] mb-8 max-w-lg mx-auto" style={{ color: '#4B5563' }}>
              Botkeeper shut down on Feb 7, 2026. Drop your last export here and we'll re-categorize, re-vendor, and re-rule everything in minutes.
            </p>

            <div
              className="rounded-2xl p-10 transition-colors cursor-pointer"
              style={{ background: file ? 'rgba(45,122,93,0.05)' : '#F7F6F2', border: `2px dashed ${file ? '#2D7A5D' : '#D1D5DB'}` }}
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleFileSelect(e.dataTransfer.files[0]); }}
              data-testid="dropzone"
            >
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.tsv,.txt"
                onChange={(e) => handleFileSelect(e.target.files[0])}
                className="hidden"
                data-testid="file-input"
              />
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText size={22} style={{ color: '#2D7A5D' }} />
                  <div className="text-left">
                    <p className="font-body text-[14px] font-semibold" style={{ color: '#0A3622' }}>{file.name}</p>
                    <p className="text-[12px]" style={{ color: '#4B5563' }}>{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); setFile(null); }} className="ml-3 p-1.5 rounded-full hover:bg-[#E5E3DB]" data-testid="remove-file">
                    <X size={14} style={{ color: '#4B5563' }} />
                  </button>
                </div>
              ) : (
                <>
                  <Upload size={22} className="mx-auto mb-3" style={{ color: '#9CA3AF' }} />
                  <p className="font-body text-[14px] font-medium" style={{ color: '#1A1A1A' }}>
                    Drop your Botkeeper export here, or click to browse
                  </p>
                  <p className="text-[12px] mt-1" style={{ color: '#4B5563' }}>CSV / TSV up to 25MB</p>
                </>
              )}
            </div>

            <button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="mt-8 inline-flex items-center gap-2 h-12 px-8 rounded-full text-[14px] font-body font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:scale-[1.02]"
              style={{ background: '#D98A4A', color: '#FFFFFF' }}
              data-testid="parse-btn"
            >
              {uploading ? 'Parsing…' : 'Parse File'} <ArrowRight size={16} />
            </button>
          </section>
        )}

        {/* STEP 2 — Map */}
        {step === 2 && staging && (
          <section className="space-y-6" data-testid="step-2">
            <div className="rounded-2xl p-7" style={{ background: '#FFFFFF', border: '1px solid #E5E3DB' }}>
              <h2 className="font-heading text-[22px] font-semibold tracking-tight mb-1" style={{ color: '#0A3622' }}>Map your columns</h2>
              <p className="text-[13.5px]" style={{ color: '#4B5563' }}>
                Detected <strong>{staging.total_rows.toLocaleString()}</strong> rows in <strong>{staging.filename}</strong>. We pre-filled best matches — adjust as needed.
              </p>

              <div className="mt-5 mb-5 p-4 rounded-xl flex items-start gap-3" style={{ background: 'rgba(217,138,74,0.07)', border: '1px solid rgba(217,138,74,0.2)' }}>
                <Sparkles size={16} style={{ color: '#D98A4A' }} className="mt-0.5 flex-shrink-0" />
                <p className="text-[13px]" style={{ color: '#4B5563' }}>
                  Auto-detected mapping based on Botkeeper export schema. SnuggleMind's hybrid AI will re-categorize each row using your existing client policies after import.
                </p>
              </div>

              {/* Target picker */}
              <div className="mb-6 p-4 rounded-xl" style={{ background: '#F7F6F2' }}>
                <p className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: '#D98A4A' }}>Import as</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { v: 'transactions', label: 'Transactions' },
                    { v: 'vendors', label: 'Vendors' },
                    { v: 'accounts', label: 'Chart of Accounts' },
                  ].map((t) => (
                    <button
                      key={t.v}
                      onClick={() => setTarget(t.v)}
                      className="h-9 px-4 rounded-full text-[12.5px] font-body font-medium transition-all"
                      style={{
                        background: target === t.v ? '#0A3622' : '#FFFFFF',
                        color: target === t.v ? '#FFFFFF' : '#1A1A1A',
                        border: `1px solid ${target === t.v ? '#0A3622' : '#E5E3DB'}`,
                      }}
                      data-testid={`target-${t.v}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {CANONICAL_FIELDS.map((f) => (
                  <div key={f.key} className="space-y-1.5">
                    <label className="text-[11px] uppercase tracking-[0.18em] font-semibold flex items-center gap-1.5" style={{ color: '#4B5563' }}>
                      {f.label}
                      {f.required && target === 'transactions' && <span style={{ color: '#D98A4A' }}>*</span>}
                    </label>
                    <select
                      value={mapping[f.key] || ''}
                      onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value || undefined })}
                      className="w-full h-10 px-3 rounded-xl text-[13.5px] font-body bg-white"
                      style={{ border: '1px solid #E5E3DB', color: '#0A3622' }}
                      data-testid={`map-${f.key}`}
                    >
                      <option value="">— skip —</option>
                      {staging.headers.map((h) => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="rounded-2xl p-7" style={{ background: '#FFFFFF', border: '1px solid #E5E3DB' }}>
              <h3 className="font-heading text-[16px] font-semibold tracking-tight mb-4" style={{ color: '#0A3622' }}>Sample (first 10 rows)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-[12px] font-mono" data-testid="preview-table">
                  <thead>
                    <tr style={{ borderBottom: '1px solid #E5E3DB' }}>
                      {staging.headers.map((h) => (
                        <th key={h} className="px-2 py-2 text-left font-semibold whitespace-nowrap" style={{ color: '#4B5563' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {staging.sample_rows.map((row, ri) => (
                      <tr key={`row-${ri}`} style={{ borderBottom: '1px solid #F7F6F2' }}>
                        {staging.headers.map((h) => (
                          <td key={h} className="px-2 py-2 whitespace-nowrap" style={{ color: '#1A1A1A' }}>
                            {String(row[h] ?? '').slice(0, 40)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <button
                onClick={() => setStep(1)}
                className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full text-[13px] font-body font-medium"
                style={{ color: '#4B5563' }}
              >
                <ArrowLeft size={14} /> Back
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={!requiredMapped}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-full text-[13.5px] font-body font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: '#0A3622', color: '#FFFFFF' }}
                data-testid="continue-to-review"
              >
                Continue <ArrowRight size={14} />
              </button>
            </div>
            {!requiredMapped && (
              <p className="text-[12px] flex items-center gap-1.5" style={{ color: '#D98A4A' }}>
                <AlertCircle size={13} /> Map both Date and Amount to continue.
              </p>
            )}
          </section>
        )}

        {/* STEP 3 — Review */}
        {step === 3 && staging && (
          <section className="space-y-6" data-testid="step-3">
            <div className="rounded-2xl p-8 text-center" style={{ background: '#FFFFFF', border: '1px solid #E5E3DB' }}>
              <h2 className="font-heading text-[24px] font-semibold tracking-tight mb-3" style={{ color: '#0A3622' }}>Ready to import.</h2>
              <p className="text-[14px] mb-6 max-w-md mx-auto" style={{ color: '#4B5563' }}>
                We'll create <strong>{staging.total_rows.toLocaleString()}</strong> {target} from <strong>{staging.filename}</strong>. Each row runs through the SnuggleMind AI engine so categories, vendors, and rules carry forward automatically.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl mx-auto mb-8">
                {mappedPairs.map(([k, v]) => (
                  <div key={k} className="p-3 rounded-xl text-left" style={{ background: '#F7F6F2' }}>
                    <p className="text-[10px] uppercase tracking-[0.16em] font-semibold mb-0.5" style={{ color: '#9CA3AF' }}>{k}</p>
                    <p className="text-[12px] font-mono truncate" style={{ color: '#0A3622' }}>{v}</p>
                  </div>
                ))}
              </div>
              <button
                onClick={handleCommit}
                disabled={committing}
                className="inline-flex items-center gap-2 h-12 px-8 rounded-full text-[14px] font-body font-semibold disabled:opacity-50 transition-all hover:scale-[1.02]"
                style={{ background: '#D98A4A', color: '#FFFFFF' }}
                data-testid="commit-btn"
              >
                {committing ? 'Importing…' : `Import ${staging.total_rows.toLocaleString()} ${target}`} <ArrowRight size={16} />
              </button>
            </div>
            <button
              onClick={() => setStep(2)}
              className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full text-[13px] font-body font-medium"
              style={{ color: '#4B5563' }}
            >
              <ArrowLeft size={14} /> Back
            </button>
          </section>
        )}

        {/* STEP 4 — Done */}
        {step === 4 && result && (
          <section className="rounded-2xl p-12 text-center" style={{ background: '#FFFFFF', border: '1px solid #E5E3DB' }} data-testid="step-4">
            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(45,122,93,0.12)' }}>
              <Check size={32} style={{ color: '#2D7A5D' }} strokeWidth={2} />
            </div>
            <h2 className="font-heading text-[28px] font-semibold tracking-tight mb-3" style={{ color: '#0A3622' }}>You're in.</h2>
            <p className="text-[15px] mb-8 max-w-md mx-auto" style={{ color: '#4B5563' }}>
              <strong style={{ color: '#0A3622' }}>{result.summary.created.toLocaleString()}</strong> {result.target} created
              {result.summary.skipped > 0 ? <>, <strong>{result.summary.skipped}</strong> skipped (invalid rows)</> : null}
              {result.summary.errors > 0 ? <>, <strong style={{ color: '#D98A4A' }}>{result.summary.errors}</strong> errors</> : null}.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
              <Link
                to={result.target === 'vendors' ? '/transactions' : result.target === 'accounts' ? '/settings' : '/transactions'}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-full text-[13.5px] font-body font-semibold"
                style={{ background: '#0A3622', color: '#FFFFFF' }}
                data-testid="view-imported"
              >
                View imported {result.target} <ArrowRight size={14} />
              </Link>
              <button
                onClick={() => { setStep(1); setFile(null); setStaging(null); setMapping({}); setResult(null); }}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-full text-[13.5px] font-body font-semibold"
                style={{ background: 'transparent', color: '#0A3622', border: '1.5px solid #0A3622' }}
                data-testid="import-another"
              >
                Import another file
              </button>
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
}
